import ClipboardJS from 'clipboard';
import tippy from 'tippy.js';
import { getData } from '../utils/ajax-utils';
import { tippyDefaultOptions } from './tippy';

/**
 * This class is for creating a predefined cart in the cart page
 */
export class PredefinedCartConstructor {
    $form: HTMLFormElement;
    $formErrorsContainer: HTMLElement;
    $descriptionInput: HTMLInputElement|null;
    $expirationDateInput: HTMLInputElement|null;
    $partnerCodeInput: HTMLInputElement|null;
    $predefinedCartContainer: HTMLElement;
    $generateBtn: HTMLButtonElement|null;
    $copyBtn: HTMLButtonElement|null;
    $input: HTMLInputElement|null;
    $fields: NodeListOf<HTMLInputElement>;

    constructor($form: HTMLFormElement, $predefinedCartContainer: HTMLElement) {
        this.$form = $form;
        this.$formErrorsContainer = Array.from(this.$form.children).filter($child => $child.classList.contains('form-error')).pop() as HTMLElement;
        this.$descriptionInput = this.$form.querySelector<HTMLInputElement>('.js-predefined-cart-description-input');
        this.$expirationDateInput = this.$form.querySelector<HTMLInputElement>('.js-predefined-cart-expiration-date-input');
        this.$partnerCodeInput = this.$form.querySelector<HTMLInputElement>('.js-partner-code-input');
        this.$predefinedCartContainer = $predefinedCartContainer;
        this.$generateBtn = $predefinedCartContainer.querySelector<HTMLButtonElement>('.js-predefined-cart-generate-button');
        this.$copyBtn = $predefinedCartContainer.querySelector<HTMLButtonElement>('.js-predefined-cart-copy-button');
        this.$input = $predefinedCartContainer.querySelector<HTMLInputElement>('.js-predefined-cart-input');
        this.$fields = this.$form.querySelectorAll<HTMLInputElement>('[data-predefined-cart-option]');

        if (this.$formErrorsContainer.children.length > 0) {
            return;
        }

        // Create the success tooltip
        if (this.$copyBtn && this.$generateBtn) {
            // Enable the generation button
            this.$generateBtn.removeAttribute('disabled');

            const successTooltip = tippy(this.$copyBtn, {
                ...tippyDefaultOptions,
                content: this.$copyBtn.dataset.clipboardSuccess,
                trigger: 'manual',
                hideOnClick: false,
                placement: 'bottom-end',
                onShow(instance) {
                    setTimeout(() => {
                        instance.hide();
                    }, 2000);
                },
            });

            // Initialize the copy action
            const clipboard = new ClipboardJS(this.$copyBtn);
            clipboard.on('success', () => {
                successTooltip.show();
            });

            const descriptionRequiredTooltip = this.$descriptionInput ? tippy(this.$descriptionInput, {
                ...tippyDefaultOptions,
                content: this.$descriptionInput.dataset.messageRequired,
                trigger: 'manual',
                hideOnClick: false,
                placement: 'bottom',
                onShow(instance) {
                    setTimeout(() => {
                        instance.hide();
                    }, 2000);
                },
            }) : null;

            this.$generateBtn.addEventListener('click', () => {
                if (this.$descriptionInput && '' === this.$descriptionInput.value.trim()) {
                    this.$descriptionInput.focus();
                    descriptionRequiredTooltip?.show();

                    return;
                }

                this.generateLink();
            });
        }
    }

    /**
     * Generate the predefined cart link
     * 
     * @return {void}
     */
    generateLink() : void {
        if (!this.$partnerCodeInput || !this.$input || !this.$copyBtn || !this.$generateBtn) {
            return;
        }

        this.$generateBtn.dispatchEvent(new CustomEvent('generate'));

        const productsOptions: { [key: string]: { [key: string]: boolean } } = {};

        this.$fields.forEach($field => {
            const itemKey = $field.closest<HTMLElement>('.js-product')?.dataset.itemKey as string;
            const optionName = $field.dataset.predefinedCartOption as string;
            const optionValue = $field.checked;

            if (typeof productsOptions[itemKey] === 'undefined') {
                productsOptions[itemKey] = {};
            }

            productsOptions[itemKey][optionName] = optionValue;
        });

        this.$descriptionInput?.setAttribute('disabled', 'disabled');
        this.$expirationDateInput?.setAttribute('disabled', 'disabled');

        const data = {
            action: 'get_predefined_cart_link',
            options: JSON.stringify(productsOptions),
            description: this.$descriptionInput ? this.$descriptionInput.value : '',
            expiration_date: this.$expirationDateInput ? this.$expirationDateInput.value : '',
            partner_code: '1' === this.$partnerCodeInput.dataset.valid ? this.$partnerCodeInput.dataset.validValue || '' : '',
        };

        getData(scriptVar.ajaxUrl, data).then(response => {
            if (typeof response.success !== 'undefined' && response.success && this.$input && this.$copyBtn) {
                this.$input.value = this.$input.dataset.baseUrl + response.data.uuid;
                this.$copyBtn.removeAttribute('disabled');
            }

            this.$generateBtn?.classList.remove('wait');
        }).catch(() => {
            this.$generateBtn?.classList.remove('wait');
        }).finally(() => {
            if (this.$descriptionInput) {
                this.$descriptionInput.removeAttribute('disabled');
                this.$descriptionInput.value = '';
            }

            if (this.$expirationDateInput) {
                this.$expirationDateInput.removeAttribute('disabled');
                this.$expirationDateInput.value = '';
            }
        });
    }
}
